import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api, {
  DocumentSignFieldInput,
  DocumentSignFieldResponse,
  DocumentSignFileResponse,
  DocumentSignRecipientResponse,
} from '@/lib/api';
import { useFields, useRecipients, useSetFields } from '../hooks';
import { FieldOverlay, type DraftField } from './FieldOverlay';
import { FieldPalette, type FieldType } from './FieldPalette';
import { PdfPageView, usePdfDocument } from './PdfPageView';
import { getRecipientColor } from './recipientColors';

interface FieldsEditorProps {
  envelopeId: string;
  files: DocumentSignFileResponse[];
  readOnly?: boolean;
}

const PAGE_RENDER_WIDTH = 720;
const DEFAULT_FIELD_FRAC: Record<FieldType, { w: number; h: number }> = {
  signature: { w: 0.25, h: 0.05 },
  initial: { w: 0.08, h: 0.05 },
  date_signed: { w: 0.15, h: 0.04 },
  signer_name: { w: 0.2, h: 0.04 },
  text: { w: 0.2, h: 0.04 },
  checkbox: { w: 0.04, h: 0.04 },
};

let scratchCounter = 0;
function nextScratchKey(): string {
  scratchCounter += 1;
  return `new-${Date.now()}-${scratchCounter}`;
}

function toDraft(field: DocumentSignFieldResponse): DraftField {
  return {
    key: field.fieldId,
    recipientId: field.recipientId,
    type: field.type,
    page: field.page,
    x: field.x,
    y: field.y,
    w: field.w,
    h: field.h,
    required: field.required,
  };
}

/**
 * Per-file field placement editor. The user picks a file tab, picks a
 * recipient + a field type, then clicks on a page to place a field. Fields
 * can be moved/resized; selecting one shows a remove handle.
 */
export function FieldsEditor({ envelopeId, files, readOnly = false }: FieldsEditorProps) {
  const sourceFiles = useMemo(() => files.filter((f) => f.kind === 'source'), [files]);
  const { recipients } = useRecipients(envelopeId);

  const [activeFileId, setActiveFileId] = useState<string | null>(null);

  useEffect(() => {
    if (sourceFiles.length === 0) {
      setActiveFileId(null);
      return;
    }
    if (!activeFileId || !sourceFiles.find((f) => f.fileId === activeFileId)) {
      setActiveFileId(sourceFiles[0].fileId);
    }
  }, [sourceFiles, activeFileId]);

  if (recipients.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Add at least one recipient before placing fields.
      </p>
    );
  }

  if (sourceFiles.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Upload a document before placing fields.</p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 border-b">
        {sourceFiles.map((file) => (
          <button
            key={file.fileId}
            type="button"
            onClick={() => setActiveFileId(file.fileId)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              activeFileId === file.fileId
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {file.fileName}
          </button>
        ))}
      </div>

      {activeFileId ? (
        <FieldsEditorTab
          key={activeFileId}
          envelopeId={envelopeId}
          file={sourceFiles.find((f) => f.fileId === activeFileId)!}
          recipients={recipients}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  );
}

interface FieldsEditorTabProps {
  envelopeId: string;
  file: DocumentSignFileResponse;
  recipients: DocumentSignRecipientResponse[];
  readOnly?: boolean;
}

function FieldsEditorTab({ envelopeId, file, recipients, readOnly }: FieldsEditorTabProps) {
  const { fields, loading } = useFields(envelopeId, file.fileId);
  const setFields = useSetFields(envelopeId, file.fileId);

  const [drafts, setDrafts] = useState<DraftField[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [activeRecipientId, setActiveRecipientId] = useState<string>(
    recipients[0]?.recipientId ?? '',
  );
  const [activeType, setActiveType] = useState<FieldType>('signature');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Stable mapping from recipientId → color index, ordered by routing order.
  const recipientIndexById = useMemo(() => {
    const map = new Map<string, number>();
    [...recipients]
      .sort((a, b) => a.routingOrder - b.routingOrder)
      .forEach((r, i) => map.set(r.recipientId, i));
    return map;
  }, [recipients]);

  useEffect(() => {
    if (loading) return;
    if (!isDirty) {
      setDrafts(fields.map(toDraft));
    }
  }, [fields, loading, isDirty]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { downloadUrl } = await api.getDocumentSignFileDownloadUrl(envelopeId, file.fileId);
        if (!cancelled) setPdfUrl(downloadUrl);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [envelopeId, file.fileId]);

  useEffect(() => {
    if (recipients.length > 0 && !recipients.find((r) => r.recipientId === activeRecipientId)) {
      setActiveRecipientId(recipients[0].recipientId);
    }
  }, [recipients, activeRecipientId]);

  const { doc: pdfDoc, error: pdfError } = usePdfDocument(pdfUrl);
  const pageCount = file.pageCount ?? pdfDoc?.numPages ?? 0;

  function placeField(page: number, xFrac: number, yFrac: number) {
    if (readOnly) return;
    if (!activeRecipientId) return;
    const def = DEFAULT_FIELD_FRAC[activeType];
    const x = Math.max(0, Math.min(1 - def.w, xFrac - def.w / 2));
    const y = Math.max(0, Math.min(1 - def.h, yFrac - def.h / 2));
    const key = nextScratchKey();
    setDrafts((prev) => [
      ...prev,
      {
        key,
        recipientId: activeRecipientId,
        type: activeType,
        page,
        x,
        y,
        w: def.w,
        h: def.h,
        required: true,
      },
    ]);
    setSelectedKey(key);
    setIsDirty(true);
  }

  function updateField(key: string, patch: Partial<DraftField>) {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
    setIsDirty(true);
  }

  function removeField(key: string) {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
    if (selectedKey === key) setSelectedKey(null);
    setIsDirty(true);
  }

  async function save() {
    setError(null);
    try {
      const payload: DocumentSignFieldInput[] = drafts.map((d) => ({
        recipientId: d.recipientId,
        type: d.type,
        page: d.page,
        x: d.x,
        y: d.y,
        w: d.w,
        h: d.h,
        required: d.required ?? true,
      }));
      await setFields.mutateAsync(payload);
      setIsDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save fields');
    }
  }

  function handlePageClick(e: MouseEvent<HTMLDivElement>, page: number, w: number, h: number) {
    if (readOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xFrac = (e.clientX - rect.left) / w;
    const yFrac = (e.clientY - rect.top) / h;
    placeField(page, xFrac, yFrac);
  }

  if (pdfError || error) {
    return <p className="text-sm text-destructive">{pdfError ?? error}</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
      <aside className="space-y-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Recipient
          </h3>
          <Select
            value={activeRecipientId}
            onValueChange={setActiveRecipientId}
            disabled={readOnly}
          >
            <SelectTrigger aria-label="Active recipient">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {recipients.map((r) => {
                const color = getRecipientColor(recipientIndexById.get(r.recipientId) ?? 0);
                return (
                  <SelectItem key={r.recipientId} value={r.recipientId}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-sm"
                        style={{ background: color.swatch }}
                        aria-hidden="true"
                      />
                      {r.name || r.email || r.recipientId}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Field type
          </h3>
          <FieldPalette selectedType={activeType} onSelect={setActiveType} />
        </div>
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={!isDirty || setFields.isPending}
            className="w-full"
          >
            Save fields
          </Button>
        )}
      </aside>

      <section className="space-y-4">
        {pdfDoc === null ? (
          <p className="text-sm text-muted-foreground">Loading document…</p>
        ) : (
          Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNumber) => (
            <PdfPageView
              key={pageNumber}
              pdfDoc={pdfDoc}
              pageNumber={pageNumber}
              width={PAGE_RENDER_WIDTH}
            >
              {({ width, height }) => (
                <div
                  className={readOnly ? '' : 'cursor-crosshair'}
                  style={{ width, height }}
                  onClick={(e) => handlePageClick(e, pageNumber, width, height)}
                  data-testid={`pdf-overlay-${pageNumber}`}
                >
                  {drafts
                    .filter((d) => d.page === pageNumber)
                    .map((d) => {
                      const recipient = recipients.find((r) => r.recipientId === d.recipientId);
                      return (
                        <FieldOverlay
                          key={d.key}
                          field={d}
                          recipientIndex={recipientIndexById.get(d.recipientId) ?? 0}
                          recipientName={recipient?.name || recipient?.email || 'Recipient'}
                          pageWidth={width}
                          pageHeight={height}
                          selected={selectedKey === d.key}
                          readOnly={readOnly}
                          onSelect={() => setSelectedKey(d.key)}
                          onChange={(next) => updateField(d.key, next)}
                          onRemove={() => removeField(d.key)}
                        />
                      );
                    })}
                </div>
              )}
            </PdfPageView>
          ))
        )}
      </section>
    </div>
  );
}
